// Quote Builder — hinge-drilling aggregation.
//
// Both frontal pickers append a priced "Drilling charge (N holes)" sundry per
// unit they commit (door-picker.ts for a door pair, sjb-cabinet-picker.ts for
// a cabinet's leaves). On a real kitchen that multiplies: the 84-line
// reference quote carries TWENTY of them, same supplier, same code, £94.64
// between them and twenty rows on every document.
//
// ── Why this is exact, not approximate ────────────────────────────────────
// The charge is priced PER HOLE and the pickers multiply it out
// (`listPrice = perHole × holeQty`, drilling-charge.ts), so every line in a
// group resolves to the same rate. On the reference quote:
//
//     4.80 / 4 holes  =  2.40 / 2 holes  =  £1.20 list
//     6.76 / 4 holes  =  3.38 / 2 holes  =  £1.69 retail
//
// Collapsing to `qty = total holes` at those per-hole figures reproduces the
// job to the penny — 56 × £1.69 = £94.64. It is not a rounding-tolerant
// "close enough": the function REFUSES to aggregate unless both the trade and
// the retail totals come back identical in integer pence, because a tidier
// quote that quietly moved the price is worse than a long one.
//
// ── Why it does not key on the markup ─────────────────────────────────────
// The first version grouped on markupMode/markupPct/markupGbp so the retail
// total would follow for free. That failed on the actual reference quote: it
// has had "Set total profit on this job" applied, which switches every line to
// £-markup with a PER-UNIT figure — 4-hole lines carry £1.96, 2-hole lines
// £0.98 — so the twenty rows split into two groups and only collapsed to two.
// (£1.96/4 = £0.98/2 = £0.49 a hole, i.e. they always did agree per hole; the
// key was reading the multiplier, not the rate.) It now derives the aggregate
// markup from the group's retail total instead, so a profit-targeted quote
// aggregates like any other.
//
// ── What is NOT aggregated ───────────────────────────────────────────────
// The per-door hole positions and fabrication instructions. Those live on each
// DOOR line's notes ("2 holes — top 98mm · bottom 98mm") and are never touched
// here — the factory reads them off the door, not off the charge. Only the
// priced sundry collapses, and `supplier` + `code` carry through
// byte-identical so purchase-order supplier matching (which matches on the
// supplier name string) and the per-supplier breakdown cannot notice.

import type { QuoteBuilderState, QuoteItem } from "./types";
import { pencify } from "./formatters";
import { calcTrade } from "./pricing";

/** Both pickers build the code as `<RANGE>-DRILLING`. */
const DRILLING_CODE_RX = /-DRILLING$/i;

/** `Drilling charge (4 holes)` → 4. */
const HOLES_IN_DESC_RX = /\((\d+)\s*holes?\)/i;

/** The description an aggregated row carries. */
export const AGGREGATED_DRILLING_DESC = "Hinge drilling";

export function isDrillingSundry(it: QuoteItem): boolean {
  return it.category === "sundries" && DRILLING_CODE_RX.test(it.code || "");
}

/** SJB's own per-hole door-drilling processing codes (sjb-processing.csv
 *  `2026CIDDABS` / `2026CIDDBID`). The SJB PANEL picker commits its hinge
 *  drilling under one of these rather than as `<RANGE>-DRILLING`, so
 *  `isDrillingSundry` never saw it — panel drilling was missing from the
 *  Totals "of which hinge drilling" figure and sat inside the supplier's
 *  product figures. */
const SJB_HOLE_DRILL_CODE_RX = /CIDD(ABS|BID)$/i;

/** `Hinge drilling — 4 holes (25mm panel)` → 4. The panel picker's shape: a
 *  qty-1 line whose price is already per-hole × holes. */
const HOLES_AFTER_DASH_RX = /—\s*(\d+)\s*holes?\b/i;

/**
 * Every hinge-drilling CHARGE, whichever picker wrote it — for DISPLAY only
 * (the Totals breakdown and the per-supplier table).
 *
 * Deliberately NOT what aggregation keys on: `aggregateDrilling` stays on
 * `isDrillingSundry`, because the panel line is priced as one qty-1 line and
 * collapsing it into a per-hole row is a pricing change nobody has checked.
 */
export function isDrillingCharge(it: QuoteItem): boolean {
  return isDrillingSundry(it)
    || (it.category === "sundries" && SJB_HOLE_DRILL_CODE_RX.test(it.code || ""));
}

/** Holes a drilling charge represents, for display. 0 when it cannot be told. */
export function drillingChargeHoles(it: QuoteItem): number {
  if (isDrillingSundry(it)) return drillingHoles(it) ?? 0;
  if (!isDrillingCharge(it)) return 0;
  const m = HOLES_AFTER_DASH_RX.exec(it.desc || "");
  return m ? Number(m[1]) * (it.qty || 0) : 0;
}

/** Aggregation multiplies `qty`, and `sjbHardwareTotal` is a PER-UNIT addend
 *  that `lineTrade()` adds outside the discount — a line carrying one would
 *  have its hardware multiplied by the hole count. Drilling sundries never
 *  carry hardware, but "never" is worth one line of code. */
function isAggregatable(it: QuoteItem): boolean {
  return isDrillingSundry(it) && !(it.sjbHardwareTotal || 0);
}

/**
 * How many holes this line represents in total, or null when it cannot be told
 * from the line alone.
 *
 * An ALREADY-aggregated line states its holes in `qty` against a per-hole
 * `listPrice`, so it has no "(N holes)" in its description — that is the
 * no-match case, and `qty` is the answer. Which is what makes this idempotent:
 * re-running over a quote that is already collapsed is a no-op.
 */
export function drillingHoles(it: QuoteItem): number | null {
  const qty = it.qty || 0;
  if (qty <= 0) return null;
  const m = HOLES_IN_DESC_RX.exec(it.desc || "");
  if (!m) return qty;
  const per = Number(m[1]);
  if (!Number.isFinite(per) || per <= 0) return null;
  return per * qty;
}

/** Retail value of every drilling sundry on the quote, in pence. Sums BOTH
 *  shapes, so a quote that has not been through aggregation yet still reports
 *  the right figure in the Totals panel. */
export function drillingRetailPence(state: QuoteBuilderState): number {
  let p = 0;
  for (const z of state.zones) {
    for (const it of z.items) {
      if (isDrillingCharge(it)) p += pencify(it.retailPrice || 0) * (it.qty || 0);
    }
  }
  return p;
}

/** Total holes across every drilling sundry on the quote. */
export function drillingHoleCount(state: QuoteBuilderState): number {
  let n = 0;
  for (const z of state.zones) {
    for (const it of z.items) {
      n += drillingChargeHoles(it);
    }
  }
  return n;
}

/**
 * The drilling share of each supplier's row in the per-supplier breakdown,
 * keyed exactly as `quoteTotals().bySupplier` is (`supplier || "Manual"`), so
 * the table can show products and drilling as separate rows that still sum to
 * the supplier's total. `qty` is the line quantity those charges contributed
 * to the supplier's Items figure — an aggregated row's qty IS its hole count,
 * which is how drilling used to inflate Items.
 */
export function drillingBySupplier(
  state: QuoteBuilderState,
): Record<string, { tradePence: number; retailPence: number; qty: number; holes: number }> {
  const out: Record<string, { tradePence: number; retailPence: number; qty: number; holes: number }> = {};
  for (const z of state.zones) {
    for (const it of z.items) {
      if (!isDrillingCharge(it)) continue;
      const qty = it.qty || 0;
      const sup = it.supplier || "Manual";
      const slot = out[sup] ?? { tradePence: 0, retailPence: 0, qty: 0, holes: 0 };
      slot.tradePence += pencify(it.tradePrice || 0) * qty;
      slot.retailPence += pencify(it.retailPrice || 0) * qty;
      slot.qty += qty;
      slot.holes += drillingChargeHoles(it);
      out[sup] = slot;
    }
  }
  return out;
}

/**
 * Non-markup pricing parameters that must match for a collapse to be
 * value-preserving. `discPct` is in here because trade is derived from
 * list × (1 − disc), so blending two discounts would move the trade cost.
 */
function pricingKey(it: QuoteItem): string {
  return (it.discPct || 0).toFixed(2);
}

/**
 * Can the group's "Reset" snapshot survive being rescaled?
 *
 * `preTargetMarkup*` is the per-unit markup from before a profit target was
 * applied, and Reset restores it. A %-mode snapshot is scale-invariant (the
 * markup is a ratio of trade, so per-hole and per-door give the same retail),
 * so it carries over untouched. A £-mode snapshot is per-unit and would be
 * multiplied by the hole count — Reset would then misprice the line. Rather
 * than rescale a field whose whole job is to be a faithful record, refuse.
 */
function resetSnapshotSurvives(rows: QuoteItem[]): boolean {
  const snapshotted = rows.filter((r) => r.preTargetMarkupMode !== undefined);
  if (snapshotted.length === 0) return true;
  if (snapshotted.length !== rows.length) return false;
  const first = snapshotted[0]!;
  if (first.preTargetMarkupMode !== "pct") return false;
  return snapshotted.every((r) =>
    r.preTargetMarkupMode === "pct"
    && (r.preTargetMarkupPct ?? 0) === (first.preTargetMarkupPct ?? 0));
}

/**
 * Collapse the drilling sundries into one row per
 * (zone, supplier, code, discount) group. Mutates `state`; returns true if
 * anything changed. Idempotent.
 *
 * Runs on ADD, so it is forward-only: a quote saved before this shipped keeps
 * its historical rows until the next door lands on it. Deliberate — nothing
 * here reaches out and rewrites a sent quote. `drillingRetailPence` reads both
 * shapes, so an untidied quote still reports correctly in the Totals panel.
 */
export function aggregateDrilling(state: QuoteBuilderState): boolean {
  const groups = new Map<string, QuoteItem[]>();
  for (const z of state.zones) {
    for (const it of z.items) {
      if (!isAggregatable(it)) continue;
      // ZONE is part of the key. The brief asked for one row per supplier per
      // QUOTE; scoping per zone as well is a deliberate, documented
      // deviation. Collapsing across zones would move the utility room's
      // drilling into the kitchen — and a zone flipped to "summary" for the
      // customer PDF prints its zone total as ONE line, so the utility would
      // quote low and the kitchen high with nothing on the document to show
      // why. On a single-room quote (the reported case, and the 84-line
      // reference quote) this still yields exactly one row.
      const key = `${z.id}\u0000${it.supplier}\u0000${it.code}\u0000${pricingKey(it)}`;
      const slot = groups.get(key) ?? [];
      slot.push(it);
      groups.set(key, slot);
    }
  }

  const dropIds = new Set<string>();

  for (const rows of groups.values()) {
    if (rows.length < 2) continue;
    if (!resetSnapshotSurvives(rows)) continue;

    let holes = 0;
    let listPence = 0;
    let tradePence = 0;
    let retailPence = 0;
    let bail = false;
    for (const r of rows) {
      const h = drillingHoles(r);
      if (h === null || h <= 0) { bail = true; break; }
      const qty = r.qty || 0;
      holes += h;
      listPence   += pencify(r.listPrice   || 0) * qty;
      tradePence  += pencify(r.tradePrice  || 0) * qty;
      retailPence += pencify(r.retailPrice || 0) * qty;
    }
    if (bail || holes <= 0 || listPence <= 0 || retailPence <= 0) continue;

    // Per-hole figures must be whole pence, or the collapse cannot reproduce
    // the totals and we leave the long list alone.
    if (listPence % holes !== 0 || tradePence % holes !== 0 || retailPence % holes !== 0) continue;
    const perHoleList   = (listPence / holes) / 100;
    const perHoleTrade  = (tradePence / holes) / 100;
    const perHoleRetail = (retailPence / holes) / 100;

    // Trade has to fall out of list × (1 − disc) on its own, because that is
    // what recalcItem will recompute the moment anything touches the line.
    const keep = rows[0]!;
    if (pencify(calcTrade(perHoleList, keep.discPct || 0)) !== tradePence / holes) continue;

    // A top-hung flap is drilled in the TOP of the unit, not the side, and
    // sjb-cabinet-picker.ts says so on the per-cabinet charge. Aggregating
    // would have thrown that sentence away — the frontal line still carries
    // it, but a factory reading the charge row should not have to go looking
    // for the one instruction that changes which face gets drilled.
    const hasTopHung = rows.some((r) => /top[- ]hung|top edge/i.test(r.notes || ""));

    const before: QuoteItem = { ...keep };
    keep.qty = holes;
    keep.listPrice = perHoleList;
    keep.tradePrice = perHoleTrade;
    keep.desc = AGGREGATED_DRILLING_DESC;
    keep.notes = `${holes} hole${holes === 1 ? "" : "s"} at ${
      new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(perHoleList)
    } per hole · positions on each door line${
      hasTopHung ? " · includes a top-hung flap (top edge, not the side)" : ""}`;
    keep.retailPrice = perHoleRetail;
    // Express the aggregate markup in BOTH forms off the per-hole figures, so
    // the row reads correctly whichever mode it is in and the £/% toggle does
    // not change the price. Mode is preserved when every row agreed on %
    // (a fresh quote); otherwise £, which is the only faithful form for a
    // quote that has had a profit target applied.
    const allPct = rows.every((r) => r.markupMode === "pct"
      && (r.markupPct || 0) === (rows[0]!.markupPct || 0));
    keep.markupGbp = +(perHoleRetail - perHoleTrade).toFixed(2);
    keep.markupPct = perHoleTrade > 0
      ? +(((perHoleRetail / perHoleTrade) - 1) * 100).toFixed(2)
      : 0;
    keep.markupMode = allPct ? "pct" : "gbp";

    // Last line of defence. If any total moved, put the line back exactly as
    // it was and keep the long list — cheaper to check than to explain.
    const okList   = pencify(keep.listPrice)   * keep.qty === listPence;
    const okTrade  = pencify(keep.tradePrice)  * keep.qty === tradePence;
    const okRetail = pencify(keep.retailPrice) * keep.qty === retailPence;
    if (!okList || !okTrade || !okRetail) {
      Object.assign(keep, before);
      continue;
    }

    for (const r of rows.slice(1)) dropIds.add(r.id);
  }

  if (dropIds.size === 0) return false;
  for (const z of state.zones) z.items = z.items.filter((it) => !dropIds.has(it.id));
  return true;
}
